import * as OBC from "@thatopen/components";
import * as OBF from "@thatopen/components-front";
import * as BUI from "@thatopen/ui";
import * as CUI from "@thatopen/ui-obc";
import * as THREE from "three";

// What a double-click on the model does. SELECT leaves the canvas to the
// highlighter; every other tool takes the canvas over until it is put away.
export type ViewerTool =
  | "SELECT"
  | "LENGTH"
  | "AREA"
  | "ANGLE"
  | "VOLUME"
  | "SECTION";

export type MeasureTool = "LENGTH" | "AREA" | "ANGLE" | "VOLUME";

export type MeasureUnits = "IMPERIAL" | "METRIC";

export type ViewerProjection = "Perspective" | "Orthographic";

export type ViewerNavigation = "Orbit" | "FirstPerson";

type ViewerOrientation = "front" | "back" | "left" | "right" | "top" | "bottom";

// ThatOpen only knows metric units, so its labels are written in metres at
// full precision and rewritten in the chosen system as they appear.
const RAW_LABEL = /^(-?\d+\.\d{5}) (m|m2|m3)$/;
const FEET_PER_METRE = 1 / 0.3048;

// the brand purple reads against the white model and the lime selection alike
const TOOL_COLOR = new THREE.Color("#6528d7");

/**
 * The camera, measuring and sectioning tools of the standard viewer, kept out
 * of StandardScene so the scene stays about loading models and their data.
 * Everything here is ThatOpen's own component; this class only decides which
 * one owns the canvas and wires the gestures to it.
 */
export class ViewerTools {
  private components: OBC.Components;
  private world: OBC.World;
  private camera: OBC.OrthoPerspectiveCamera;
  private fragments: OBC.FragmentsManager;
  private highlighter: OBF.Highlighter;
  private container: HTMLElement;
  private clipper: OBC.Clipper;
  private measurers: Record<MeasureTool, OBF.Measurement<any, any>>;
  private activeTool: ViewerTool = "SELECT";
  private viewCube: CUI.ViewCube;
  private modelTree?: HTMLElement;
  private modelTreeContainer?: HTMLElement;
  private units: MeasureUnits = "IMPERIAL";
  private rawLabels = new Map<HTMLElement, { value: number; unit: string }>();
  private labelObserver: MutationObserver;

  constructor(
    components: OBC.Components,
    world: OBC.World,
    camera: OBC.OrthoPerspectiveCamera,
    highlighter: OBF.Highlighter,
    container: HTMLElement,
  ) {
    this.components = components;
    this.world = world;
    this.camera = camera;
    this.fragments = components.get(OBC.FragmentsManager);
    this.highlighter = highlighter;
    this.container = container;

    // Planes stop clipping when the clipper is disabled, so it stays enabled
    // and the SECTION tool only decides whether a double-click adds one.
    this.clipper = components.get(OBC.Clipper);
    this.clipper.enabled = true;
    this.clipper.config.color = TOOL_COLOR;
    this.clipper.config.opacity = 0.15;

    this.measurers = {
      LENGTH: components.get(OBF.LengthMeasurement),
      AREA: components.get(OBF.AreaMeasurement),
      ANGLE: components.get(OBF.AngleMeasurement),
      VOLUME: components.get(OBF.VolumeMeasurement),
    };
    for (const measurer of Object.values(this.measurers)) {
      measurer.world = world;
      measurer.color = TOOL_COLOR;
      measurer.enabled = false;
      measurer.rounding = 5;
    }

    this.labelObserver = new MutationObserver(this.OnLabelsChanged);
    this.labelObserver.observe(container, {
      childList: true,
      characterData: true,
      subtree: true,
    });

    // Switching projection swaps the camera object. The post-production
    // passes and every model's culling keep the one they were given, so
    // they are handed the new one, or the canvas freezes on the last frame.
    camera.projection.onChanged.add(() => {
      const renderer = world.renderer as OBF.PostproductionRenderer;
      renderer.postproduction.updateCamera();
      for (const model of this.fragments.list.values()) {
        model.useCamera(camera.three);
      }
      this.viewCube.camera = camera.three;
    });

    CUI.Manager.init();
    this.viewCube = this.CreateViewCube();
    container.append(this.viewCube);

    container.addEventListener("dblclick", this.OnDoubleClick);
    window.addEventListener("keydown", this.OnKeyDown);
  }

  Dispose() {
    this.container.removeEventListener("dblclick", this.OnDoubleClick);
    window.removeEventListener("keydown", this.OnKeyDown);
    this.labelObserver.disconnect();
    this.rawLabels.clear();
    this.UnmountModelTree();
    this.viewCube.remove();
    // Planes are removed while the world still has its renderer. Left for
    // components.dispose(), they outlive the world and the clipper throws.
    this.clipper.deleteAll();
  }

  SetActiveTool(tool: ViewerTool) {
    if (this.activeTool !== "SELECT" && this.activeTool !== "SECTION") {
      this.measurers[this.activeTool].cancelCreation();
    }

    this.activeTool = tool;
    for (const [measureTool, measurer] of Object.entries(this.measurers)) {
      measurer.enabled = measureTool === tool;
    }

    // A double-click lands two clicks first, and selecting on each would
    // zoom the camera away from the point being picked.
    this.highlighter.enabled = tool === "SELECT";
  }

  SetMeasureUnits(units: MeasureUnits) {
    this.units = units;
    for (const [label, { value, unit }] of this.rawLabels) {
      if (!label.isConnected) {
        this.rawLabels.delete(label);
        continue;
      }
      label.textContent = this.FormatMeasure(value, unit);
    }
  }

  ClearMeasurements() {
    for (const measurer of Object.values(this.measurers)) {
      measurer.list.clear();
    }
    this.world.renderer?.update();
  }

  ClearSections() {
    this.clipper.deleteAll();
    this.world.renderer?.update();
  }

  async FitModel() {
    await this.camera.fitToItems();
  }

  async SetProjection(projection: ViewerProjection) {
    // walking has no meaning without perspective, so the walk ends first
    if (
      projection === "Orthographic" &&
      this.camera.mode.id === "FirstPerson"
    ) {
      this.camera.set("Orbit");
    }
    await this.camera.projection.set(projection);
  }

  async SetNavigation(navigation: ViewerNavigation) {
    if (
      navigation === "FirstPerson" &&
      this.camera.projection.current === "Orthographic"
    ) {
      await this.SetProjection("Perspective");
    }
    this.camera.set(navigation);
  }

  async MountModelTree(container: HTMLElement) {
    if (this.modelTree && this.modelTreeContainer === container) {
      return;
    }

    this.UnmountModelTree();

    const [modelTreeTable] = CUI.tables.spatialTree({
      components: this.components,
      // An array, not the live iterator, which the table would spend on its
      // first read. Delta models hold the fragment editor's pending edits and
      // have no structure of their own; the tree gives up on reaching one.
      models: [...this.fragments.list.values()].filter(
        (model) => !model.isDeltaModel,
      ),
    });
    modelTreeTable.preserveStructureOnFilter = true;

    const modelTree = BUI.Component.create(() => {
      const onTextInput = (e: Event) => {
        const input = e.target as BUI.TextInput;
        modelTreeTable.queryString = input.value !== "" ? input.value : null;
      };

      return BUI.html`
        <bim-panel label="Model Tree">
          <bim-panel-section
            label="Spatial Structure"
            icon="mdi:file-tree"
            fixed
          >
            <bim-text-input
              @input=${onTextInput}
              placeholder="Search Element"
              debounce="250"
            ></bim-text-input>
            ${modelTreeTable}
          </bim-panel-section>
        </bim-panel>
      `;
    });

    container.replaceChildren(modelTree);
    this.modelTree = modelTree;
    this.modelTreeContainer = container;
  }

  UnmountModelTree() {
    if (this.modelTreeContainer) {
      this.modelTreeContainer.replaceChildren();
    }
    this.modelTree = undefined;
    this.modelTreeContainer = undefined;
  }

  private CreateViewCube() {
    const viewCube = document.createElement("bim-view-cube") as CUI.ViewCube;
    viewCube.camera = this.camera.three;
    viewCube.size = 56;
    viewCube.frontText = "FRONT";
    viewCube.backText = "BACK";
    viewCube.leftText = "LEFT";
    viewCube.rightText = "RIGHT";
    viewCube.topText = "TOP";
    viewCube.bottomText = "BOTTOM";

    this.camera.controls.addEventListener("update", () =>
      viewCube.updateOrientation(),
    );

    const faces: [string, ViewerOrientation][] = [
      ["frontclick", "front"],
      ["backclick", "back"],
      ["leftclick", "left"],
      ["rightclick", "right"],
      ["topclick", "top"],
      ["bottomclick", "bottom"],
    ];
    for (const [eventName, orientation] of faces) {
      viewCube.addEventListener(eventName, () => {
        void this.LookFrom(orientation);
      });
    }

    return viewCube;
  }

  private async LookFrom(orientation: ViewerOrientation) {
    if (this.fragments.list.size === 0) return;

    const boxer = this.components.get(OBC.BoundingBoxer);
    boxer.list.clear();
    boxer.addFromModels();
    const { position, target } = await boxer.getCameraOrientation(orientation);
    boxer.list.clear();

    await this.camera.controls.setLookAt(
      position.x,
      position.y,
      position.z,
      target.x,
      target.y,
      target.z,
      true,
    );
  }

  private FormatMeasure(value: number, unit: string) {
    if (this.units === "METRIC") {
      const power = unit === "m2" ? "²" : unit === "m3" ? "³" : "";
      return `${value.toFixed(2)} m${power}`;
    }

    if (unit === "m2") {
      return `${(value * FEET_PER_METRE ** 2).toFixed(2)} ft²`;
    }
    if (unit === "m3") {
      return `${(value * FEET_PER_METRE ** 3).toFixed(2)} ft³`;
    }

    // lengths read the way drawings dimension them, to the nearest 1/16"
    const sixteenths = Math.round(Math.abs(value) * FEET_PER_METRE * 192);
    const feet = Math.floor(sixteenths / 192);
    const inches = Math.floor((sixteenths % 192) / 16);
    let fraction = sixteenths % 16;
    let denominator = 16;
    while (fraction > 0 && fraction % 2 === 0) {
      fraction /= 2;
      denominator /= 2;
    }
    const sign = value < 0 ? "-" : "";
    const inchText = fraction ? `${inches} ${fraction}/${denominator}` : inches;
    return `${sign}${feet}' ${inchText}"`;
  }

  private OnLabelsChanged = (mutations: MutationRecord[]) => {
    // A label is either written in place or written first and attached
    // after, so both the changed node and anything newly added are read.
    const candidates = new Set<Node>();
    for (const mutation of mutations) {
      candidates.add(mutation.target);
      mutation.addedNodes.forEach((node) => candidates.add(node));
    }

    for (const candidate of candidates) {
      const label =
        candidate instanceof HTMLElement ? candidate : candidate.parentElement;
      if (!label || label.childElementCount > 0) continue;

      const match = label.textContent?.match(RAW_LABEL);
      if (!match) continue;

      const value = Number(match[1]);
      this.rawLabels.set(label, { value, unit: match[2] });
      label.textContent = this.FormatMeasure(value, match[2]);
    }
  };

  private OnDoubleClick = () => {
    switch (this.activeTool) {
      case "SELECT":
        return;
      case "SECTION":
        void this.clipper.create(this.world);
        return;
      default:
        void this.measurers[this.activeTool].create();
    }
  };

  private OnKeyDown = (event: KeyboardEvent) => {
    // typing in a panel's search box is not a command
    const target = event.target as HTMLElement | null;
    if (target?.closest("input, textarea, bim-text-input")) return;
    if (this.activeTool === "SELECT") return;

    if (event.key === "Delete" || event.key === "Backspace") {
      if (this.activeTool === "SECTION") {
        void this.clipper.delete(this.world);
      } else {
        void this.measurers[this.activeTool].delete();
      }
    } else if (
      event.key === "Enter" &&
      (this.activeTool === "AREA" || this.activeTool === "VOLUME")
    ) {
      this.measurers[this.activeTool].endCreation();
    }
  };
}
